import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Lock, ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "@/i18n";
import { createCheckout, getPrintShippingQuote, getStorefrontSettings } from "@/lib/api";
import { useCart } from "@/lib/cart";
import { formatPrice } from "@/lib/utils";
import { getCountries, getCountryCallingCode } from "@/lib/countries";
import { useAuth } from "@/lib/auth";

export default function Checkout() {
  const navigate = useNavigate();
  const { t, locale } = useI18n();
  const { items, subtotal } = useCart();
  const { user, initialized, checkAuth } = useAuth();
  const countries = getCountries(locale);
  const hasPrints = items.some((item) => item.category === "print");
  const [submitting, setSubmitting] = useState(false);
  const [shippingAmount, setShippingAmount] = useState(0);
  const [loadingShipping, setLoadingShipping] = useState(false);
  const [shippingError, setShippingError] = useState(false);
  const [checkoutNote, setCheckoutNote] = useState("");
  const [emailConfirmation, setEmailConfirmation] = useState("");
  const [form, setForm] = useState({ customerName: "", customerEmail: "", customerPhone: "", phoneCountry: "", address: "", city: "", postalCode: "", administrativeArea: "", country: "", notes: "" });
  const requiresAdministrativeArea = ["US", "CA", "AU"].includes(form.country);
  const postalCodeLabel = form.country === "IT" ? t.checkout.postalCode : form.country === "US" ? t.checkout.zipCode : t.checkout.postcode;
  const administrativeAreaLabel = form.country === "US" ? t.checkout.state : form.country === "CA" ? t.checkout.province : t.checkout.stateTerritory;

  useEffect(() => {
    if (!initialized) void checkAuth();
  }, [initialized, checkAuth]);

  useEffect(() => {
    if (user?.email) setForm((current) => current.customerEmail ? current : { ...current, customerEmail: user.email });
  }, [user?.email]);

  useEffect(() => {
    getStorefrontSettings().then((settings) => setCheckoutNote(settings.checkout_note.trim())).catch(() => undefined);
  }, []);

  useEffect(() => {
    if (!hasPrints || !form.country) {
      setShippingAmount(0);
      setLoadingShipping(false);
      setShippingError(false);
      return;
    }
    let cancelled = false;
    setLoadingShipping(true);
    setShippingError(false);
    setShippingAmount(0);
    void getPrintShippingQuote(form.country).then((quote) => {
      if (!cancelled) setShippingAmount(quote.amount);
    }).catch(() => {
      if (!cancelled) setShippingError(true);
    }).finally(() => {
      if (!cancelled) setLoadingShipping(false);
    });
    return () => { cancelled = true; };
  }, [form.country, hasPrints]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!items.length) return;
    if (!user && form.customerEmail.trim().toLowerCase() !== emailConfirmation.trim().toLowerCase()) {
      toast.error(t.checkout.emailsDoNotMatch);
      return;
    }
    setSubmitting(true);
    try {
      const country = countries.find((item) => item.code === form.country);
      const locality = requiresAdministrativeArea
        ? `${form.city}, ${form.administrativeArea} ${form.postalCode}`
        : `${form.postalCode} ${form.city}`;
      const shippingAddress = `${form.address}, ${locality}, ${country?.name || form.country}`;
      const result = await createCheckout({
        items: items.map((item) => ({
          artworkId: item.id,
          quantity: item.quantity,
          ...(item.printSize ? { printSize: item.printSize } : {}),
        })),
        customerName: form.customerName,
        customerEmail: form.customerEmail,
        customerPhone: `${getCountryCallingCode(form.phoneCountry)} ${form.customerPhone.trim()}`,
        shippingAddress,
        shippingCountry: form.country,
        notes: form.notes || undefined,
        locale,
      });
      window.location.assign(result.url);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t.common.error);
      setSubmitting(false);
    }
  };

  if (!items.length) return (
    <div className="checkout-page"><div className="checkout-empty">
      <ShoppingBag size={36} /><h1>{t.cart.empty}</h1>
      <Link to="/gallery">{t.cart.continueShopping}</Link>
    </div></div>
  );

  return (
    <div className="checkout-page">
      <button onClick={() => navigate(-1)} className="checkout-back"><ArrowLeft size={15} />{t.common.cancel}</button>
      <div className="checkout-layout container">
        <form onSubmit={handleSubmit} className="checkout-form">
          <span className="gallery-index">Dalila Di Maria · Secure checkout</span>
          <h1>{t.checkout.title}</h1>
          {checkoutNote && <p className="checkout-notice" role="status">{checkoutNote}</p>}
          <div className="checkout-fields two-columns">
            <label>{t.checkout.name} *<input required autoComplete="name" value={form.customerName} onChange={(e) => setForm({ ...form, customerName: e.target.value })} /></label>
            <label>{t.checkout.email} *<input required type="email" autoComplete="email" value={form.customerEmail} onChange={(e) => setForm({ ...form, customerEmail: e.target.value })} /></label>
          </div>
          {!user && <div className="checkout-fields two-columns">
            <label>{t.checkout.confirmEmail} *<input required type="email" autoComplete="email" value={emailConfirmation} onChange={(e) => setEmailConfirmation(e.target.value)} /></label>
            <small>{t.checkout.guestEmailHelp}</small>
          </div>}
          <div className="checkout-fields">
            <label>{t.checkout.address} *<input required autoComplete="street-address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></label>
          </div>
          <div className="checkout-fields three-columns">
            <label>{t.checkout.city} *<input required autoComplete="address-level2" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} /></label>
            <label>{postalCodeLabel} *<input required autoComplete="postal-code" value={form.postalCode} onChange={(e) => setForm({ ...form, postalCode: e.target.value })} /></label>
            <label>{t.checkout.country} *<select required autoComplete="country" value={form.country} onChange={(e) => { const country = e.target.value; setForm({ ...form, country, phoneCountry: country, administrativeArea: ["US", "CA", "AU"].includes(country) ? form.administrativeArea : "" }); }}><option value="">{locale === "it" ? "Seleziona un paese" : "Select a country"}</option>{countries.map((country) => <option key={country.code} value={country.code}>{country.name}</option>)}</select></label>
          </div>
          {requiresAdministrativeArea && <div className="checkout-fields">
            <label>{administrativeAreaLabel} *<input required autoComplete="address-level1" value={form.administrativeArea} onChange={(e) => setForm({ ...form, administrativeArea: e.target.value })} /></label>
          </div>}
          <label className="checkout-notes">{t.checkout.notes}<textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></label>
          <div className="checkout-fields">
            <label>{t.checkout.phone} *<div className="flex gap-2"><select required aria-label={locale === "it" ? "Prefisso telefonico" : "Telephone prefix"} className="min-w-36" value={form.phoneCountry} onChange={(e) => setForm({ ...form, phoneCountry: e.target.value })}><option value="">{locale === "it" ? "Prefisso" : "Prefix"}</option>{countries.map((country) => <option key={country.code} value={country.code}>{getCountryCallingCode(country.code)} · {country.name}</option>)}</select><input required className="min-w-0 flex-1" type="tel" autoComplete="tel-national" inputMode="tel" placeholder="333 123 4567" pattern="[0-9 ()-]{4,20}" value={form.customerPhone} onChange={(e) => setForm({ ...form, customerPhone: e.target.value })} /></div><small>{locale === "it" ? "Seleziona il prefisso internazionale del tuo numero." : "Select your number's international dialling prefix."}</small></label>
          </div>
          <button type="submit" disabled={submitting || loadingShipping || shippingError} className="checkout-pay">
            {submitting ? t.checkout.processing : t.checkout.pay}<Lock size={15} />
          </button>
          <p className="checkout-secure"><Lock size={12} />{t.checkout.secure}</p>
        </form>

        <aside className="checkout-summary">
          <span className="gallery-index">{t.cart.title}</span>
          <div className="checkout-summary-items">{items.map((item) => (
            <article key={item.id}>
              <img src={item.image} alt={item.title} />
              <div><span>{item.category}</span><h2>{item.title}</h2>{item.printSize && <p>{item.printSize}</p>}<p>{item.quantity} × {formatPrice(item.price, item.currency)}</p></div>
            </article>
          ))}</div>
          <div className="checkout-breakdown">
            <div><span>{t.cart.subtotal}</span><strong>{formatPrice(subtotal, items[0].currency)}</strong></div>
            <div><span>{t.cart.shipping}</span><strong>{!hasPrints || form.country === "IT" ? t.checkout.shippingIncluded : !form.country ? t.checkout.shippingCalculated : loadingShipping ? t.checkout.shippingCalculating : shippingError ? t.common.error : formatPrice(shippingAmount, items[0].currency)}</strong></div>
          </div>
          <div className="checkout-total"><span>{t.cart.total}</span><strong>{formatPrice(subtotal + shippingAmount, items[0].currency)}</strong></div>
        </aside>
      </div>
    </div>
  );
}
